import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Text,
    Button,
    Badge,
    Input,
    Table,
    Card,
} from '@chakra-ui/react';
import {
    SelectRoot,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValueText,
    SelectLabel,
} from './ui/select';
import {toaster} from "./ui/toaster.jsx";
import { Field } from './ui/field.jsx';
import { getAllTrades } from '../api';
import { createListCollection } from '@chakra-ui/react';
import PageHeader from './PageHeader';
import FilterBar from './FilterBar';

const Trades = () => {
    const [trades, setTrades] = useState([]);
    const [filters, setFilters] = useState({
        symbol: '',
        trade_type: '',
        reason: '',
        strategy: '',
        start_date: '',
        end_date: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    // 创建交易类型选择集合
    const tradeTypeCollection = createListCollection({
        items: [
            { value: '', label: '全部' },
            { value: 'ENTRY', label: '入场' },
            // 后端/DB 使用 FULL_EXIT；保留 UI 文案为“出场”
            { value: 'FULL_EXIT', label: '出场' },
            { value: 'PARTIAL_EXIT', label: '部分出场' },
        ],
    });

    // 创建原因选择集合
    const reasonCollection = createListCollection({
        items: [
            { value: '', label: '全部' },
            { value: 'SIGNAL', label: '信号' },
            { value: 'STOP_LOSS', label: '止损' },
            { value: 'TAKE_PROFIT', label: '止盈' },
        ],
    });

    // 创建策略选择集合
    const strategyCollection = createListCollection({
        items: [
            { value: '', label: '全部' },
            { value: 'opening', label: '开盘' },
        ],
    });

     // 从选择组件返回的复杂对象中提取简单值的通用函数
     const extractValueFromSelection = (selectionValue) => {
        // 如果为空，返回空字符串
        if (!selectionValue) return '';
        
        // 如果是简单字符串，直接返回
        if (typeof selectionValue === 'string') return selectionValue;
        
        // 如果是有value属性的对象
        if (selectionValue.value !== undefined) {
            // 如果value是数组，取第一个元素
            return Array.isArray(selectionValue.value) 
                ? selectionValue.value[0] 
                : selectionValue.value;
        }
        
        // 如果是数组，取第一个元素
        if (Array.isArray(selectionValue)) return selectionValue[0];
        
        // 不符合上述任何格式，返回空字符串
        return '';
    };

    // 获取交易记录
    const fetchTrades = async () => {
        try {
            setIsLoading(true);
            console.log('正在获取交易记录，筛选条件:', filters);
            
            // 使用通用函数提取值
            const tradeTypeValue = extractValueFromSelection(filters.trade_type);
            const reasonValue = extractValueFromSelection(filters.reason);
            const strategyValue = extractValueFromSelection(filters.strategy);
            
            // 明确构建简单的参数对象
            const params = {
                symbol: filters.symbol || '',
                trade_type: tradeTypeValue,
                reason: reasonValue,
                strategy: strategyValue,
                start_date: filters.start_date || '',
                end_date: filters.end_date || '',
            };
            
            console.log('发送请求参数:', params);
            
            const response = await getAllTrades(params);
            console.log('获取到的交易记录:', response);
            
            // 处理可能的嵌套数据结构
            const data = response.trades ? response.trades : response;
            setTrades(data);
        } catch (error) {
            console.error('获取交易记录失败:', error);
            toaster.create({
                title: "获取交易记录失败",
                description: error.message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 初始加载和筛选条件变化时获取数据
    useEffect(() => {
        fetchTrades();
    }, []);

    // 处理筛选条件变化
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 应用筛选
    const applyFilters = () => {
        fetchTrades();
    };

    // 重置筛选
    const resetFilters = () => {
        setFilters({
            symbol: '',
            trade_type: '',
            reason: '',
            strategy: '',
            start_date: '',
            end_date: '',
        });
        // 重置后立即获取数据
        fetchTrades();
    };

    // 刷新数据
    const handleRefresh = async () => {
        try {
            setIsLoading(true);
            await fetchTrades();
            toaster.create({
                title: "刷新成功",
                description: "交易记录已更新",
                type: 'success',
            });
        } catch (error) {
            console.error('刷新失败:', error);
            toaster.create({
                title: "刷新失败",
                description: error.message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 获取交易类型徽章
    const getTradeTypeBadge = (type) => {
        const colors = {
            'ENTRY': 'blue',
            // 后端: FULL_EXIT；兼容历史数据: EXIT
            'FULL_EXIT': 'green',
            'EXIT': 'green',
            'PARTIAL_EXIT': 'yellow',
        };
        const text = {
            'ENTRY': '入场',
            'FULL_EXIT': '出场',
            'EXIT': '出场',
            'PARTIAL_EXIT': '部分出场',
        };
        return (
            <Badge colorPalette={colors[type] || 'gray'}>
                {text[type] || type || '-'}
            </Badge>
        );
    };

    // 获取原因徽章
    const getReasonBadge = (reason) => {
        const colors = {
            'SIGNAL': 'blue',
            'STOP_LOSS': 'red',
            'TAKE_PROFIT': 'green',
            'MANUAL': 'purple',
        };
        const text = {
            'SIGNAL': '信号',
            'STOP_LOSS': '止损',
            'TAKE_PROFIT': '止盈',
        };
        return (
            <Badge colorPalette={colors[reason] || 'gray'}>
                {text[reason] || reason}
            </Badge>
        );
    };

    // 格式化日期时间
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    return (
        <Box>
            <PageHeader
                title="交易记录"
                description="查看历史交易与出入场信息"
                actions={
                    <Button onClick={handleRefresh} isLoading={isLoading}>
                        刷新
                    </Button>
                }
            />

            <FilterBar
                left={
                    <>
                        <Input
                            placeholder="搜索标的"
                            value={filters.symbol}
                            onChange={(e) => handleFilterChange('symbol', e.target.value)}
                            maxW="200px"
                        />
                        <SelectRoot
                            collection={tradeTypeCollection}
                            value={filters.trade_type}
                            onValueChange={(value) => handleFilterChange('trade_type', value)}
                            width="180px"
                            size="md"
                        >
                            <SelectTrigger>
                                <SelectValueText placeholder="选择交易类型" />
                            </SelectTrigger>
                            <SelectContent position="absolute" zIndex={1000}>
                                {tradeTypeCollection.items.map((item) => (
                                    <SelectItem key={item.value} item={item}>
                                        {item.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </SelectRoot>
                    </>
                }
                right={
                    <Flex gap={2}>
                        <Button onClick={applyFilters}>应用筛选</Button>
                        <Button variant="outline" onClick={resetFilters}>重置</Button>
                    </Flex>
                }
                summary="支持按标的与交易类型筛选"
            />

            <Card.Root>
                <Card.Body>
                    <Box overflowX="auto">
                        <Table.Root size="sm" interactive>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>ID</Table.ColumnHeader>
                                    <Table.ColumnHeader>标的</Table.ColumnHeader>
                                    <Table.ColumnHeader>交易类型</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>数量</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>入场价</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>出场价</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>盈亏</Table.ColumnHeader>
                                    <Table.ColumnHeader>交易时间</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {trades.length > 0 ? (
                                    trades.map((trade) => (
                                        <Table.Row key={trade.id}>
                                            <Table.Cell py={2}>{trade.id}</Table.Cell>
                                            <Table.Cell py={2}>{trade.symbol}</Table.Cell>
                                            <Table.Cell py={2}>{getTradeTypeBadge(trade.trade_type)}</Table.Cell>
                                            <Table.Cell py={2} isNumeric>{trade.quantity}</Table.Cell>
                                            <Table.Cell py={2} isNumeric>{trade.entry_price || '-'}</Table.Cell>
                                            <Table.Cell py={2} isNumeric>{trade.exit_price || '-'}</Table.Cell>
                                            <Table.Cell py={2} isNumeric>
                                                {trade.profit_loss !== null && trade.profit_loss !== undefined ? (
                                                    <Text color={Number(trade.profit_loss) >= 0 ? 'green.500' : 'red.500'}>
                                                        ${Number(trade.profit_loss).toFixed(2)}
                                                    </Text>
                                                ) : '-'}
                                            </Table.Cell>
                                            <Table.Cell py={2} whiteSpace="nowrap">{formatDateTime(trade.trade_time)}</Table.Cell>
                                        </Table.Row>
                                    ))
                                ) : (
                                    <Table.Row>
                                        <Table.Cell colSpan={11} textAlign="center" py={4}>
                                            {isLoading ? '加载中...' : '没有找到交易记录'}
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                </Card.Body>
            </Card.Root>
        </Box>
    );
};

export default Trades; 
